import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { ActivitiesRepository } from '../domain/activities.repository';
import { CreateActivityInput, TimeActivity } from '../models/activity.model';

interface ActivityRow {
  id: number;
  name: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

@Injectable()
export class SupabaseActivitiesRepository extends ActivitiesRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async getActivityDomains(): Promise<Array<{ id: number; domainId: number | null }>> {
    const { data, error } = await this.client.from('time_activities').select('id,domain_id');
    if (error) throw error;
    return (data ?? []).map(row => ({ id: row.id, domainId: row.domain_id }));
  }

  async assignActivityDomain(id: number, domainId: number): Promise<void> {
    const { data, error } = await this.client.from('time_activities')
      .select('domain_id').eq('id', id).single();
    if (error) throw error;
    if (data.domain_id !== null && data.domain_id !== domainId) {
      throw new Error('El dominio de la actividad es distinto del dominio existente.');
    }
    if (data.domain_id === null) {
      const result = await this.client.from('time_activities')
        .update({ domain_id: domainId }).eq('id', id);
      if (result.error) throw result.error;
    }
  }

  async getActivities(): Promise<TimeActivity[]> {
    const { data, error } = await this.client
      .from('time_activities')
      .select('id,name,archived_at,created_at,updated_at')
      .order('name', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(row => this.mapActivity(row as ActivityRow));
  }

  async createActivity(input: CreateActivityInput): Promise<TimeActivity> {
    const { data: activityId, error } = await this.client.rpc('resolve_time_activity', {
      p_activity_id: null,
      p_name: input.name
    });
    if (error) throw error;

    const { data, error: loadError } = await this.client
      .from('time_activities')
      .select('id,name,archived_at,created_at,updated_at')
      .eq('id', Number(activityId))
      .single();
    if (loadError) throw loadError;

    return this.mapActivity(data as ActivityRow);
  }

  async renameActivity(id: number, name: string): Promise<void> {
    const { data, error } = await this.client
      .from('time_activities')
      .update({ name })
      .eq('id', id)
      .select('id');

    if (error?.code === '23505') {
      throw new Error('Ya existe una actividad con este nombre.');
    }
    if (error) throw error;
    if (!data?.length) throw new Error('Actividad no encontrada.');
  }

  async setActivityArchived(id: number, archived: boolean): Promise<void> {
    const { error } = await this.client
      .from('time_activities')
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .eq('id', id);

    if (error) throw error;
  }

  private mapActivity(row: ActivityRow): TimeActivity {
    return {
      id: row.id,
      name: row.name,
      archivedAt: row.archived_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
